'use client'

import * as React from 'react'
import { useTranslations } from 'next-intl'
import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

export interface ComboboxOption {
  label: string
  value: string
  searchValue?: string
  description?: string
  disabled?: boolean
  level?: number
  icon?: React.ReactNode
}

interface ComboboxProps {
  options: ComboboxOption[]
  value?: string | string[]
  onValueChange?: (value: string | string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  className?: string
  icon?: React.ReactNode
  multiple?: boolean
  clearable?: boolean
  error?: boolean
  id?: string
  onSearchChange?: (search: string) => void
}

export function Combobox({
  options = [],
  value,
  onValueChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  disabled = false,
  className,
  icon,
  multiple = false,
  clearable = false,
  error = false,
  id,
  onSearchChange
}: ComboboxProps) {
  const t = useTranslations()
  const listId = React.useId()
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')

  const valueArray = multiple ? (value && typeof value === 'object' && 'length' in value ? value : []) : []
  const selectedOption = !multiple ? options.find(option => option.value === value) : null
  const selectedCount = multiple ? valueArray.length : 0

  // CheckIcon if there's a value to clear
  const hasValue = multiple ? valueArray.length > 0 : value && value !== '' && value !== undefined

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          id={id}
          aria-controls={listId}
          aria-expanded={open}
          aria-invalid={error ? 'true' : undefined}
          disabled={disabled}
          className={cn(
            'border-input dark:bg-input/30 flex h-9 w-full cursor-pointer items-center justify-between overflow-hidden rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none disabled:cursor-not-allowed disabled:opacity-50',
            error
              ? 'border-destructive focus:border-destructive focus:ring-destructive/20 dark:focus:ring-destructive/40 focus:ring-[3px]'
              : 'focus:border-ring focus:ring-ring/50 focus:ring-[3px]',
            !value && 'text-muted-foreground',
            className
          )}>
          <div className="flex min-w-0 items-center gap-2 overflow-hidden">
            {icon}
            {multiple ? (
              selectedCount > 0 ? (
                `${selectedCount} ${t('selected')}`
              ) : (
                placeholder || t('select')
              )
            ) : selectedOption ? (
              <div className="flex min-w-0 flex-col items-start overflow-hidden">
                <span className="w-full truncate text-left">{selectedOption.label}</span>
                {selectedOption.description && (
                  <span className="text-muted-foreground w-full truncate text-left text-xs">
                    {selectedOption.description}
                  </span>
                )}
              </div>
            ) : (
              placeholder || t('select')
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {clearable && hasValue && (
              <div
                className="mr-1 flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-full bg-gray-100 text-gray-500 opacity-70 hover:opacity-100"
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (multiple) {
                    onValueChange?.([])
                  } else {
                    onValueChange?.('')
                  }
                }}
                onMouseDown={e => {
                  e.preventDefault()
                  e.stopPropagation()
                }}>
                <XIcon className="h-3 w-3" />
              </div>
            )}
            <ChevronDownIcon className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        align="start"
        style={{
          minWidth: 'var(--radix-popper-anchor-width)',
          width: 'max-content'
        }}>
        <Command
          shouldFilter={!onSearchChange}
          filter={(value, search) => {
            if (!search) {
              return 1
            }
            return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
          }}>
          <CommandInput
            placeholder={searchPlaceholder || t('search')}
            value={search}
            onValueChange={value => {
              setSearch(value)
              onSearchChange?.(value)
            }}
          />
          <CommandList id={listId} className="max-h-75">
            <CommandEmpty>{emptyText || t('not_found')}</CommandEmpty>
            <CommandGroup>
              {options.map(option => (
                <CommandItem
                  key={option.value}
                  value={option.searchValue || option.value}
                  disabled={option.disabled}
                  onSelect={currentValue => {
                    const selectedOption = options.find(opt => (opt.searchValue || opt.value) === currentValue)
                    if (selectedOption) {
                      if (multiple) {
                        const currentValues = value && typeof value === 'object' && 'length' in value ? value : []
                        const newValues = currentValues.includes(selectedOption.value)
                          ? currentValues.filter(v => v !== selectedOption.value)
                          : [...currentValues, selectedOption.value]
                        onValueChange?.(newValues)
                      } else {
                        const newValue = selectedOption.value === value ? '' : selectedOption.value
                        onValueChange?.(newValue)
                        setOpen(false)
                      }
                    }
                  }}>
                  <CheckIcon
                    className={cn(
                      'mr-2 h-4 w-4',
                      multiple
                        ? valueArray.includes(option.value)
                          ? 'opacity-100'
                          : 'opacity-0'
                        : value === option.value
                          ? 'opacity-100'
                          : 'opacity-0'
                    )}
                  />
                  {option.icon && <span className="mr-2">{option.icon}</span>}
                  <div className="flex min-w-0 flex-col items-start">
                    <span className="truncate" style={{ paddingLeft: option.level ? `${option.level * 18}px` : '0' }}>
                      {option.label}
                    </span>
                    {option.description && (
                      <span className="text-muted-foreground truncate text-xs">{option.description}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
